import React, { useEffect, useRef, useState } from 'react';

import {
  Button,
  Tag,
  Space,
  Typography,
  Modal,
  message,
} from 'antd';

import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  FormOutlined,
} from '@ant-design/icons';

import { toastSuccess, toastError } from '../Toast/ToastComponent';

import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { FaArrowLeft } from 'react-icons/fa6';
import { useAuth } from '../../Context/AuthContext';

import style from '../../Styles/AddUser.module.css';

// COMPONENTS

import TrainingsOverView from './TrainingsOverView';
import TrainingsAttendance from './TrainingsAttendance';
import TrainingsFeedback from './TrainingsFeedback';
import TrainingsReports from './TrainingsReports';
import TraineeAttendance from './TraineeAttendance';
import TraineeFeedback from './TraineeFeedback';

import AttendancePopup from './AttendancePopup';

import { TrainingsApi } from '../../Api/TrainingsApi';

const { Title } = Typography;

// ================= TABS =================

const TABS = [
  {
    key: '1',
    label: 'Overview',
  },

  {
    key: '2',
    label: 'Attendance',
  },

  {
    key: '3',
    label: 'Feedbacks',
  },

  {
    key: '4',
    label: 'Reports',
  },
];

const TRAINEE_TABS = [
  {
    key: '2',
    label: 'Attendance',
  },
  {
    key: '3',
    label: 'Feedbacks',
  },
];

const TrainingsView = () => {
  const navigate = useNavigate();

  const location = useLocation();
  const { authState } = useAuth();

  const isTrainee = authState?.role_id === 4 || authState?.role_name?.toLowerCase() === 'trainee' || location?.state?.isTrainee;
  const displayTabs = isTrainee ? TRAINEE_TABS : TABS;

  // ================= DATA =================

  const overviewData =
    location?.state?.overviewData || {};

  // ================= STATE =================
  const [trainingDetails, setTrainingDetails] = useState(overviewData?.training_details || {});
  const trainingId = trainingDetails?.training_id || trainingDetails?.id;

  const [activeTab, setActiveTab] =
    useState(isTrainee ? '2' : '1');

  const [
    attendancePopupVisible,
    setAttendancePopupVisible,
  ] = useState(false);
  
  const [refreshKey, setRefreshKey] = useState(0);

  // ================= SYNC TAB WITH URL =================
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab) setActiveTab(tab);
  }, [location.search]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    navigate(`${location.pathname}?tab=${key}`, {
      state: location.state,
      replace: true,
    });
  };

  // Refs to hold export functions registered by TrainingsReports
  const excelFnRef = useRef(null);
  const pdfFnRef  = useRef(null);

  // ================= END TRAINING =================

  const handleEndTraining = () => {
    Modal.confirm({
      title: 'End Training',
      content: 'Are you sure you want to end this training?',
      okText: 'Yes, End It',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const payload = {
            training_id: trainingId,
          };
          const res = await TrainingsApi.endTraining(payload);
          if (res?.data?.success) {
            toastSuccess(res?.data?.message || 'Training ended successfully');
            navigate('/trainings');
          } else {
            toastError(res?.data?.message || 'Failed to end training');
          }
        } catch (err) {
          toastError(err?.response?.data?.message || err?.message || 'Something went wrong');
        }
      },
    });
  };

  // ================= TAB CONTENT =================

  const renderContent = () => {
    switch (activeTab) {
      case '1':
        return (
          <TrainingsOverView />
        );

      case '2':
        return isTrainee ? (
          <TraineeAttendance key={refreshKey} onDataLoad={(data) => setTrainingDetails(data)} />
        ) : (
          <TrainingsAttendance key={refreshKey} />
        );

      case '3':
        return isTrainee ? (
          <TraineeFeedback />
        ) : (
          <TrainingsFeedback />
        );

      case '4':
        return (
          <TrainingsReports
            onRegisterExcel={(fn) => { excelFnRef.current = fn; }}
            onRegisterPDF={(fn) => { pdfFnRef.current = fn; }}
          />
        );

      default:
        return (
          <TrainingsOverView />
        );
    }
  };

  return (
    <div
      style={{
        background: '#f5f5f5',
        minHeight: '100vh',
        padding: '0px',
      }}
    >
      {/* ================= HEADER ================= */}

      <div
        className="d-flex px-0 pb-1 align-items-center"
        style={{
          padding: '14px 18px',
          marginBottom: '20px',
        }}
      >
        <div
          className={`${style['arrow-icon']} border px-2 pb-1`}
          onClick={() => navigate(-1)}
          style={{ cursor: 'pointer' }}
        >
          <FaArrowLeft />
        </div>

        <div className={`mx-3 ${style['add-user']} mb-0`}>
          {trainingDetails?.training_name || 'Training'}
        </div>

        <Tag
          style={{
            borderRadius: '20px',
            padding: '1px 15px',
            fontSize: '13px',
            fontWeight: '500',
            background: trainingDetails?.status ? '#e6f7e9' : '#fff1f0',
            color: trainingDetails?.status ? '#28a745' : '#f5222d',
            border: `1px solid ${trainingDetails?.status ? '#D1E7DD' : '#ffa39e'}`,
            margin: 0,
          }}
        >
          {trainingDetails?.status ? 'Active' : 'Inactive'}
        </Tag>

        {/* RIGHT BUTTONS */}
        <div style={{ marginLeft: 'auto' }}>
          <Space size="middle">
            {/* REPORTS */}

            {activeTab === '4' ? (
              <>
                <Button
                  icon={
                    <DownloadOutlined />
                  }
                  onClick={() =>
                    excelFnRef.current &&
                    excelFnRef.current()
                  }
                >
                  Export Excel
                </Button>

                {!isTrainee && (
                  <Button
                    type="primary"
                    icon={
                      <DownloadOutlined />
                    }
                    onClick={() =>
                      pdfFnRef.current &&
                      pdfFnRef.current()
                    }
                  >
                    Export PDF
                  </Button>
                )}
              </>
            ) : activeTab === '3' ? (
              <>
                {/* FEEDBACK TAB */}

                {!isTrainee && trainingDetails?.status && (
                  <Button
                    danger
                    icon={
                      <CloseCircleOutlined />
                    }
                    onClick={handleEndTraining}
                  >
                    End Training
                  </Button>
                )}

                <Button
                  type="primary"
                  icon={
                    <FormOutlined />
                  }
                  onClick={() =>
                    isTrainee
                      ? navigate('/trainee_review', {
                          state: {
                            training_id: trainingId,
                            trainee_id: authState?.id,
                            mode: 'create',
                          },
                        })
                      : navigate('/trainings_review', {
                          state: {
                            trainingId: trainingId,
                          },
                        })
                  }
                >
                  Write a Review
                </Button>
              </>
            ) : (
              <>
                {/* OTHER TABS */}

                {!isTrainee && trainingDetails?.status && (
                  <>
                    <Button
                      danger
                      icon={
                        <CloseCircleOutlined />
                      }
                      onClick={handleEndTraining}
                    >
                      End Training
                    </Button>

                    <Button
                      type="primary"
                      icon={
                        <CheckCircleOutlined />
                      }
                      onClick={() =>
                        setAttendancePopupVisible(
                          true
                        )
                      }
                    >
                      Mark Attendance
                    </Button>
                  </>
                )}
              </>
            )}
          </Space>
        </div>
      </div>

      {/* ================= TABS ================= */}

      <div
        style={{
          background: '#fff',
          borderTop:
            '1px solid #f0f0f0',
        }}
      >
      <div
  style={{
    display: 'flex',
    alignItems: 'center',
    minHeight: '40px', // reduced minimum height
  }}
>
  {displayTabs.map((tab) => {
    const isActive =
      activeTab === tab.key;

    return (
      <div
        key={tab.key}
        onClick={() =>
          handleTabChange(tab.key)
        }
        style={{
          padding: '14px 18px', // reduced padding
          minHeight: '38px', // reduced tab height
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          fontSize: 14,
          fontWeight:
            isActive ? 600 : 500,
          color: isActive
            ? '#1b67b3'
            : '#595959',
          borderBottom: isActive
            ? '2px solid #1b67b3'
            : '2px solid transparent',
          transition: '0.2s',
          userSelect: 'none',
          boxSizing: 'border-box',
        }}
      >
        {tab.label}
      </div>
    );
  })}
</div>
      </div>

      {/* ================= CONTENT ================= */}

      <div
        style={{
          marginTop: 20,
          borderRadius: 6,
        }}
      >
        {renderContent()}
      </div>

      {/* ================= ATTENDANCE POPUP ================= */}

      <AttendancePopup
        visible={
          attendancePopupVisible
        }
        onClose={(success) => {
          setAttendancePopupVisible(false);
          if (success) {
            setRefreshKey(prev => prev + 1);
            handleTabChange('2');
          }
        }}
      />
    </div>
  );
};

export default TrainingsView;